/**
 * Transactional reconciliation for AI form drafts.
 *
 * The AI endpoint returns a complete form draft. This module validates that
 * draft before touching Backbone, replaces the configurable state exactly,
 * verifies the result, and restores the original live state if any step
 * fails. Runtime Backbone objects stay attached to their owning models.
 *
 */
/* global Backbone, define, jQuery, nfRadio, _ */
define( 'controllers/ai/aiDraftReconciler',[], function () {
	'use strict';

	const runtimeAttributes = {
		editActive: true,
		fieldContentsData: true,
		formContentData: true,
		idAttribute: true,
		jBox: true,
		objectDomain: true,
		objectType: true,
	};

	/**
	 * Clone JSON-compatible builder configuration without retaining references.
	 *
	 * @param {*} value Value to clone.
	 * @return {*} Independent clone.
	 */
	const cloneConfiguration = function ( value ) {
		return JSON.parse( JSON.stringify( value ) );
	};

	/**
	 * Builder-only bookkeeping the option repeater attaches to every row.
	 *
	 * errors and max_options are optionRepeaterModel defaults (validation
	 * state, and a limit every reader takes from the setting definition, not
	 * the row), and settingModel is the whole setting definition, attached to
	 * each row on add. None of it is form configuration: serialized, it ships
	 * markup and the template One/Two/Three rows to the provider on every
	 * exchange, and a provider dropping it from rows it authors would fail
	 * exact verification.
	 */
	const nestedRuntimeKeys = [ 'errors', 'max_options', 'settingModel' ];

	/**
	 * Convert one live Backbone model into plain configuration.
	 *
	 * Nested option collections are configuration presented through a runtime
	 * Backbone.Collection. Serialize their rows while retaining the collection
	 * itself during reconciliation.
	 *
	 * @param {Object} model Live model.
	 * @return {Object} Plain configuration.
	 */
	const modelData = function ( model ) {
		const data = _.omit( _.clone( model.toJSON() ), 'jBox' );
		_.each( data, function ( value, key ) {
			if ( value instanceof Backbone.Collection ) {
				data[ key ] = _.map( value.toJSON(), function ( row ) {
					return isRecord( row )
						? _.omit( row, nestedRuntimeKeys )
						: row;
				} );
			}
		} );
		return cloneConfiguration( data );
	};

	/**
	 * Serialize the live form at this instant.
	 *
	 * Request snapshots replace browser-only temporary IDs with negative
	 * integers accepted by ability schemas, retaining the real ID privately for
	 * an exact mapping when the result returns.
	 *
	 * @param {boolean} forRequest Whether the result is leaving the browser.
	 * @return {{fields: Array, actions: Array, settings: Object}} Complete draft.
	 */
	const serialize = function ( forRequest ) {
		let nextID = -1;
		/**
		 * Serialize one builder collection.
		 *
		 * @param {Object} collection Live collection.
		 * @return {Array} Plain configuration rows.
		 */
		const serializeCollection = function ( collection ) {
			if ( ! collection ) {
				return [];
			}
			return collection.map( function ( model ) {
				const data = modelData( model );
				if ( forRequest && ! jQuery.isNumeric( data.id ) ) {
					data._client_id = data.id;
					data.id = nextID;
					nextID -= 1;
				}
				return data;
			} );
		};
		const settings = nfRadio
			.channel( 'settings' )
			.request( 'get:settings' );
		const settingsData =
			settings && settings.toJSON ? settings.toJSON() : {};

		return {
			fields: serializeCollection(
				nfRadio.channel( 'fields' ).request( 'get:collection' )
			),
			actions: serializeCollection(
				nfRadio.channel( 'actions' ).request( 'get:collection' )
			),
			settings: cloneConfiguration(
				_.omit( settingsData, 'formContentData', 'fieldContentsData' )
			),
		};
	};

	/**
	 * Determine whether a value is a plain record.
	 *
	 * @param {*} value Candidate value.
	 * @return {boolean} True for a plain record.
	 */
	const isRecord = function ( value ) {
		return (
			value &&
			'[object Object]' === Object.prototype.toString.call( value )
		);
	};

	/**
	 * Validate the complete top-level shape before any mutation begins.
	 *
	 * @param {Object} draft Candidate form draft.
	 * @return {string} Empty when valid; otherwise a diagnostic.
	 */
	const validateShape = function ( draft ) {
		if ( ! isRecord( draft ) ) {
			return 'The form draft is not an object.';
		}
		if ( ! _.isArray( draft.fields ) ) {
			return 'The form draft has no complete field list.';
		}
		if ( ! _.isArray( draft.actions ) ) {
			return 'The form draft has no complete action list.';
		}
		if ( ! isRecord( draft.settings ) ) {
			return 'The form draft has no complete settings record.';
		}
		if ( ! _.every( draft.fields, isRecord ) ) {
			return 'The form draft contains an invalid field.';
		}
		if ( ! _.every( draft.actions, isRecord ) ) {
			return 'The form draft contains an invalid action.';
		}
		return '';
	};

	/**
	 * Resolve a target row to exactly one current model.
	 *
	 * @param {Object} collection Live collection.
	 * @param {Object} data       Target row.
	 * @return {Object|null} Matching model, if present.
	 */
	const resolveModel = function ( collection, data ) {
		if ( data._client_id ) {
			return collection.get( data._client_id ) || null;
		}
		if ( 'undefined' === typeof data.id || null === data.id ) {
			return null;
		}
		return collection.get( data.id ) || null;
	};

	/**
	 * Complete nested repeater rows from the type default's row shape.
	 *
	 * A provider authors repeater rows as bare configuration (an option is a
	 * label and a value, sometimes a calc), while every builder-authored row
	 * also carries the structural keys of its columns: a selected state and
	 * an explicit order. Fill the absent structural keys, with order
	 * defaulting to the row's position, so replacement never installs rows
	 * the builder's sorting and the front end must guess about. The ability
	 * schema always writes label and value, so only structural keys are ever
	 * actually absent.
	 *
	 * @param {Array}  rows       Draft repeater rows; mutated in place.
	 * @param {Object} defaultRow One row of the type's default value.
	 * @return {Array} The same rows, completed.
	 */
	const completeNestedRows = function ( rows, defaultRow ) {
		return _.map( rows, function ( row, index ) {
			if ( ! isRecord( row ) ) {
				return row;
			}
			_.each( defaultRow, function ( value, key ) {
				if ( 'undefined' !== typeof row[ key ] ) {
					return;
				}
				if ( 'order' === key ) {
					row[ key ] = index;
					return;
				}
				row[ key ] = _.isObject( value )
					? cloneConfiguration( value )
					: value;
			} );
			return row;
		} );
	};

	/**
	 * Fill a row's absent settings from its type's defaults.
	 *
	 * A provider returns only the settings it decided to speak about, so a
	 * newly requested field or action arrives without the baseline keys every
	 * builder row carries. The builder's own add path fills those from the
	 * type's settingDefaults; exact replacement would otherwise strip them
	 * back off, leaving a model the builder's views cannot render.
	 *
	 * Completion happens before both replacement and verification so the
	 * expected and actual states stay comparable.
	 *
	 * @param {string} domain fields|actions.
	 * @param {Object} data   Draft row.
	 * @return {Object} The same row, with absent baseline settings filled.
	 */
	const completeRow = function ( domain, data ) {
		const type = data.type;
		if ( ! type ) {
			return data;
		}
		const rowType = nfRadio.channel( domain ).request( 'get:type', type );
		if ( ! rowType ) {
			return data;
		}
		_.each( rowType.get( 'settingDefaults' ), function ( value, key ) {
			if ( 'undefined' === typeof data[ key ] ) {
				// Clone so a shared default array is never aliased into a row.
				data[ key ] = _.isObject( value )
					? cloneConfiguration( value )
					: value;
				return;
			}
			if (
				_.isArray( value ) &&
				value.length &&
				isRecord( value[ 0 ] ) &&
				_.isArray( data[ key ] )
			) {
				data[ key ] = completeNestedRows( data[ key ], value[ 0 ] );
			}
		} );

		return data;
	};

	/**
	 * Prepare an exact collection plan without mutating live state.
	 *
	 * @param {Object}  collection Live collection.
	 * @param {Array}   rows       Complete target rows.
	 * @param {boolean} restoring  Allow rows removed by a failed attempt to be recreated.
	 * @param {string}  domain     fields|actions.
	 * @return {{error: string, entries: Array}} Prepared plan.
	 */
	const prepareCollection = function ( collection, rows, restoring, domain ) {
		if ( ! collection ) {
			return {
				error: 'The live collection is unavailable.',
				entries: [],
			};
		}
		const claimed = {};
		const identifiers = {};
		const entries = [];
		let error = '';

		_.each( rows, function ( data ) {
			if ( error ) {
				return;
			}
			let identifier = data._client_id || data.id;
			if (
				'undefined' === typeof identifier ||
				null === identifier ||
				'' === identifier
			) {
				error = 'A draft row has no identifier.';
				return;
			}
			identifier = String( identifier );
			if ( identifiers[ identifier ] ) {
				error = 'The draft contains duplicate row identifiers.';
				return;
			}
			identifiers[ identifier ] = true;
			const model = resolveModel( collection, data );
			if ( model && claimed[ model.cid ] ) {
				error =
					'The draft maps more than one row to the same live model.';
				return;
			}
			const newRow =
				true === data._ai_new ||
				( jQuery.isNumeric( data.id ) && Number( data.id ) < 0 );
			if ( ! model && ! newRow && ! restoring ) {
				error =
					'The draft references a live model that does not exist.';
				return;
			}
			if ( model ) {
				claimed[ model.cid ] = true;
			}
			/*
			 * Completion applies to incoming provider rows only. A restore
			 * replays state this builder already captured, and must reproduce
			 * it exactly rather than the captured state plus defaults.
			 */
			const prepared = cloneConfiguration( data );
			entries.push( {
				data: restoring ? prepared : completeRow( domain, prepared ),
				model,
			} );
		} );

		return { error, entries };
	};

	/**
	 * Preserve a runtime attribute that is not form configuration.
	 *
	 * @param {string} key   Attribute key.
	 * @param {*}      value Current live value.
	 * @return {boolean} Whether the attribute must remain attached.
	 */
	const isRuntimeAttribute = function ( key, value ) {
		return (
			true === runtimeAttributes[ key ] ||
			value instanceof Backbone.Collection ||
			value instanceof Backbone.Model ||
			'function' === typeof value
		);
	};

	/**
	 * Reset live collections in place from their serialized arrays.
	 *
	 * Serializing the draft flattens a live Backbone collection to a plain
	 * array. Setting that array back over the collection would replace the
	 * collection with an array, and the builder calls collection methods on
	 * these later. Handled keys are removed from attrs so the set that follows
	 * does not undo the reset.
	 *
	 * @param {Object} model Live model.
	 * @param {Object} attrs Attributes about to be set; handled keys are deleted.
	 * @return {void}
	 */
	const resetLiveCollections = function ( model, attrs ) {
		_.each( attrs, function ( value, key ) {
			const current = model.get( key );
			if (
				_.isArray( value ) &&
				current instanceof Backbone.Collection
			) {
				current.reset( value );
				delete attrs[ key ];
			}
		} );
	};

	/**
	 * Replace one model's configurable state exactly.
	 *
	 * @param {Object}  model            Live model.
	 * @param {Object}  source           Target row.
	 * @param {boolean} preserveTargetID Whether the target ID is a real live ID.
	 * @return {void}
	 */
	const replaceModel = function ( model, source, preserveTargetID ) {
		const attrs = _.omit(
			cloneConfiguration( source ),
			'_client_id',
			'_ai_new'
		);
		if ( source._client_id ) {
			attrs.id = source._client_id;
		}
		if ( ! preserveTargetID && ! source._client_id ) {
			/*
			 * The row's negative id is the provider's placeholder, not an
			 * identity. The builder minted a real one when it added the model,
			 * and serialize maps that into _client_id so the next exchange can
			 * find the row again. Dropping it here leaves the model with no
			 * identity at all: the following request sends a bare placeholder,
			 * the server allocates the same one for its own new row, and the
			 * draft arrives with two rows sharing an identifier.
			 */
			const minted = model.get( 'id' );
			if ( 'undefined' === typeof minted || null === minted ) {
				delete attrs.id;
			} else {
				attrs.id = minted;
			}
		}

		_.each( model.attributes, function ( current, key ) {
			if ( Object.prototype.hasOwnProperty.call( attrs, key ) ) {
				return;
			}
			if ( current instanceof Backbone.Collection ) {
				current.reset( [] );
				return;
			}
			if ( ! isRuntimeAttribute( key, current ) ) {
				model.unset( key, { silent: true } );
			}
		} );

		resetLiveCollections( model, attrs );
		model.set( attrs );
		/*
		 * The builder reacts to its own change events, and some of those
		 * rewrite a neighbouring setting: changing a label regenerates a
		 * non-manual field key, for instance. The draft is the agreed state, so
		 * put back anything the reaction moved away from it. One corrective
		 * pass only; a value that still disagrees is a genuine mismatch and
		 * verification is left to reject the exchange.
		 */
		const corrections = {};
		_.each( attrs, function ( value, key ) {
			if ( ! _.isEqual( model.get( key ), value ) ) {
				corrections[ key ] = value;
			}
		} );
		if ( ! _.isEmpty( corrections ) ) {
			model.set( corrections );
		}
	};

	/**
	 * Add one target row through the builder's normal domain request.
	 *
	 * @param {string}  domain    fields|actions.
	 * @param {Object}  source    Target row.
	 * @param {boolean} restoring Whether an original browser ID must be retained.
	 * @return {Object|null} Added model.
	 */
	const addModel = function ( domain, source, restoring ) {
		const attrs = _.omit(
			cloneConfiguration( source ),
			'_client_id',
			'_ai_new'
		);
		const isTemporaryAIID =
			jQuery.isNumeric( attrs.id ) && Number( attrs.id ) < 0;
		if ( isTemporaryAIID && ! restoring ) {
			delete attrs.id;
		}
		if ( 'actions' === domain && isTemporaryAIID && ! restoring ) {
			attrs.id = nfRadio.channel( 'actions' ).request( 'get:tmpID' );
		}
		if ( 'fields' === domain ) {
			return (
				nfRadio
					.channel( 'fields' )
					.request( 'add', attrs, false, false ) || null
			);
		}
		return nfRadio.channel( 'actions' ).request( 'add', attrs ) || null;
	};

	/**
	 * Apply a prepared complete collection plan.
	 *
	 * @param {string}  domain     fields|actions.
	 * @param {Object}  collection Live collection.
	 * @param {Array}   entries    Prepared entries.
	 * @param {boolean} restoring  Whether this is an internal rollback.
	 * @return {Array} Models corresponding to target row order.
	 */
	const applyCollection = function (
		domain,
		collection,
		entries,
		restoring
	) {
		const kept = {};
		const ordered = [];

		_.each( entries, function ( entry ) {
			const model =
				entry.model || addModel( domain, entry.data, restoring );
			if ( ! model ) {
				throw new Error(
					'The builder did not add a requested ' + domain + ' row.'
				);
			}
			const preserveTargetID =
				restoring ||
				!! entry.data._client_id ||
				( jQuery.isNumeric( entry.data.id ) &&
					Number( entry.data.id ) >= 0 );
			replaceModel( model, entry.data, preserveTargetID );
			entry.model = model;
			kept[ model.cid ] = true;
			ordered.push( model );
		} );

		_.each(
			collection.filter( function ( model ) {
				return ! kept[ model.cid ];
			} ),
			function ( model ) {
				/*
				 * The domain delete requests immediately start an asynchronous
				 * preview save. Removing directly keeps this transaction entirely
				 * in memory until every domain has applied and verified; the normal
				 * drawer-close/publish boundary saves the complete draft later.
				 */
				if ( 'fields' === domain ) {
					nfRadio
						.channel( 'fields' )
						.trigger( 'delete:field', model );
				}
				collection.remove( model );
			}
		);
		collection.sort();
		/*
		 * Both domains redraw. The canvas already reacted to field changes
		 * through this request; the actions tab had no equivalent, so an
		 * Emails & Actions tab left rendered while chat worked kept its
		 * pre-change toggles, and in some sequences repainted only the row's
		 * greyed styling, showing a disabled action whose toggle still read on.
		 * The stored state was right throughout; only the drawing was stale.
		 */
		nfRadio.channel( domain ).request( 'redraw:collection' );
		return ordered;
	};

	/**
	 * Fill absent baseline keys on rows destined for a settings collection.
	 *
	 * Calculations reach the live model as a plain array and are installed by
	 * resetLiveCollections, whose reset() applies the collection model's own
	 * defaults to every row. A calculation the assistant mints arrives without
	 * them, so the row the builder ends up holding carries two keys the draft
	 * does not, and exact verification refuses the whole exchange. An existing
	 * calculation had already been through the collection once and so came back
	 * wearing both, which is why editing one worked while creating one never
	 * could.
	 *
	 * This is the completion already done for field and action rows, reaching
	 * the collections that arrive through settings rather than through their
	 * own domain. Completing the draft is the fix rather than loosening the
	 * comparison, which would give up the exactness the all-or-nothing contract
	 * rests on.
	 *
	 * Skipped while restoring, for the same reason row completion is: a restore
	 * replays state this builder already captured and must reproduce it exactly,
	 * not that state plus defaults.
	 *
	 * @param {Object}  settings  Complete configurable settings; mutated in place.
	 * @param {boolean} restoring Whether this is replaying captured state.
	 * @return {Object} The same settings, with collection rows completed.
	 */
	const completeSettingsCollections = function ( settings, restoring ) {
		if ( restoring || ! isRecord( settings ) ) {
			return settings;
		}
		const model = nfRadio.channel( 'settings' ).request( 'get:settings' );
		if ( ! model || ! model.get ) {
			return settings;
		}
		_.each( settings, function ( rows, key ) {
			if ( ! _.isArray( rows ) ) {
				return;
			}
			const live = model.get( key );
			if ( ! ( live instanceof Backbone.Collection ) || ! live.model ) {
				return;
			}
			const proto = live.model.prototype || {};
			const defaults = _.isFunction( proto.defaults )
				? proto.defaults()
				: proto.defaults;
			if ( ! isRecord( defaults ) ) {
				return;
			}
			_.each( rows, function ( row ) {
				if ( ! isRecord( row ) ) {
					return;
				}
				_.each( defaults, function ( value, name ) {
					if ( 'undefined' !== typeof row[ name ] ) {
						return;
					}
					row[ name ] = _.isObject( value )
						? cloneConfiguration( value )
						: value;
				} );
			} );
		} );

		return settings;
	};

	/**
	 * Replace form settings while retaining builder-owned runtime collections.
	 *
	 * @param {Object} settings Complete configurable settings.
	 * @return {void}
	 */
	const applySettings = function ( settings ) {
		const model = nfRadio.channel( 'settings' ).request( 'get:settings' );
		if ( ! model || ! model.set ) {
			throw new Error( 'The live settings model is unavailable.' );
		}
		const attrs = _.omit(
			cloneConfiguration( settings ),
			'formContentData',
			'fieldContentsData'
		);
		_.each( model.attributes, function ( current, key ) {
			if ( Object.prototype.hasOwnProperty.call( attrs, key ) ) {
				return;
			}
			if ( ! isRuntimeAttribute( key, current ) ) {
				model.unset( key, { silent: true } );
			}
		} );
		resetLiveCollections( model, attrs );
		model.set( attrs );
	};

	/**
	 * Convert a model to comparable configuration for one target row.
	 *
	 * @param {Object} model Live model.
	 * @return {Object} Comparable configuration.
	 */
	const comparableModel = function ( model ) {
		return modelData( model );
	};

	/**
	 * Describe a mismatch precisely enough to triage from the console.
	 *
	 * The stage sentence alone says what failed but not on what, and reading
	 * the difference previously meant rebuilding the bundle with
	 * instrumentation. Name the differing keys and both sides of each.
	 *
	 * @param {Object} actual   Comparable live state.
	 * @param {Object} expected Comparable draft state.
	 * @return {string} Keys and values of every disagreement.
	 */
	const describeMismatch = function ( actual, expected ) {
		const keys = _.filter(
			_.union( _.keys( actual ), _.keys( expected ) ),
			function ( key ) {
				return ! _.isEqual( actual[ key ], expected[ key ] );
			}
		);
		return (
			' Differing keys: ' +
			keys.join( ', ' ) +
			'. Draft: ' +
			JSON.stringify( _.pick( expected, keys ) ) +
			' Live: ' +
			JSON.stringify( _.pick( actual, keys ) )
		);
	};

	/**
	 * Verify one collection, including exact target order and identifiers.
	 *
	 * @param {Object} collection Live collection.
	 * @param {Array}  entries    Applied entries.
	 * @return {string} Empty when verified; otherwise a diagnostic.
	 */
	const verifyCollection = function ( collection, entries ) {
		if ( collection.length !== entries.length ) {
			return (
				'The live collection length does not match the draft.' +
				' Live rows: ' +
				collection.length +
				', draft rows: ' +
				entries.length +
				'.'
			);
		}
		let error = '';
		_.each( entries, function ( entry, index ) {
			if ( error ) {
				return;
			}
			if ( collection.at( index ) !== entry.model ) {
				error =
					'The live collection order does not match the draft.' +
					' First out-of-place row: ' +
					( index + 1 ) +
					'.';
				return;
			}
			const expected = _.omit(
				cloneConfiguration( entry.data ),
				'_client_id',
				'_ai_new'
			);
			const actual = comparableModel( entry.model );
			if ( entry.data._client_id ) {
				expected.id = entry.data._client_id;
			}
			if (
				true === entry.data._ai_new ||
				( jQuery.isNumeric( entry.data.id ) &&
					Number( entry.data.id ) < 0 )
			) {
				delete expected.id;
				delete actual.id;
			}
			_.each( actual, function ( value, key ) {
				if ( Object.prototype.hasOwnProperty.call( expected, key ) ) {
					return;
				}
				if ( isRuntimeAttribute( key, entry.model.get( key ) ) ) {
					delete actual[ key ];
				}
			} );
			if ( ! _.isEqual( actual, expected ) ) {
				error =
					'A live model does not exactly match the draft.' +
					' Row ' +
					( index + 1 ) +
					'.' +
					describeMismatch( actual, expected );
			}
		} );
		return error;
	};

	/**
	 * Verify exact configurable form settings.
	 *
	 * @param {Object} expected Target settings.
	 * @return {string} Empty when verified; otherwise a diagnostic.
	 */
	const verifySettings = function ( expected ) {
		const model = nfRadio.channel( 'settings' ).request( 'get:settings' );
		const actual = _.omit(
			cloneConfiguration( model.toJSON() ),
			'formContentData',
			'fieldContentsData'
		);
		_.each( actual, function ( value, key ) {
			if ( Object.prototype.hasOwnProperty.call( expected, key ) ) {
				return;
			}
			if ( isRuntimeAttribute( key, model.get( key ) ) ) {
				delete actual[ key ];
			}
		} );
		return _.isEqual( actual, expected )
			? ''
			: 'The live form settings do not exactly match the draft.' +
					describeMismatch( actual, expected );
	};

	/**
	 * Preserve collection bookkeeping so a failed transaction has no residue.
	 *
	 * @param {Object} collection Live collection.
	 * @return {Object} Bookkeeping snapshot.
	 */
	const captureBookkeeping = function ( collection ) {
		return {
			newIDs: cloneConfiguration( collection.newIDs || [] ),
			removedIDs: cloneConfiguration( collection.removedIDs || {} ),
			tmpNum: collection.tmpNum,
		};
	};

	/**
	 * Restore collection bookkeeping after a failed transaction rollback.
	 *
	 * @param {Object} collection Live collection.
	 * @param {Object} state      Captured state.
	 * @return {void}
	 */
	const restoreBookkeeping = function ( collection, state ) {
		collection.newIDs = state.newIDs;
		collection.removedIDs = state.removedIDs;
		collection.tmpNum = state.tmpNum;
	};

	/**
	 * Reconcile and verify a complete draft.
	 *
	 * @param {Object}  draft     Complete target draft.
	 * @param {boolean} restoring Whether missing original rows may be recreated.
	 * @return {void}
	 */
	const reconcile = function ( draft, restoring ) {
		const fields = nfRadio.channel( 'fields' ).request( 'get:collection' );
		const actions = nfRadio
			.channel( 'actions' )
			.request( 'get:collection' );
		const fieldPlan = prepareCollection(
			fields,
			draft.fields,
			restoring,
			'fields'
		);
		const actionPlan = prepareCollection(
			actions,
			draft.actions,
			restoring,
			'actions'
		);
		if ( fieldPlan.error ) {
			throw new Error( fieldPlan.error );
		}
		if ( actionPlan.error ) {
			throw new Error( actionPlan.error );
		}

		const orderedFields = applyCollection(
			'fields',
			fields,
			fieldPlan.entries,
			restoring
		);
		const orderedActions = applyCollection(
			'actions',
			actions,
			actionPlan.entries,
			restoring
		);
		// Before apply, so the settings the builder installs and the settings
		// verification compares against are the same completed rows.
		completeSettingsCollections( draft.settings, restoring );
		applySettings( draft.settings );
		fieldPlan.entries = _.map(
			fieldPlan.entries,
			function ( entry, index ) {
				entry.model = orderedFields[ index ];
				return entry;
			}
		);
		actionPlan.entries = _.map(
			actionPlan.entries,
			function ( entry, index ) {
				entry.model = orderedActions[ index ];
				return entry;
			}
		);

		const error =
			verifyCollection( fields, fieldPlan.entries ) ||
			verifyCollection( actions, actionPlan.entries ) ||
			verifySettings( draft.settings );
		if ( error ) {
			throw new Error( error );
		}
	};

	/**
	 * Apply one complete draft as an all-or-nothing browser transaction.
	 *
	 * @param {Object}  draft     Complete target draft.
	 * @param {boolean} restoring Whether missing trusted rows may be recreated.
	 * @return {{success: boolean, before: Object|null, wasClean: boolean, error: Error|null}} Result.
	 */
	const applyDraft = function ( draft, restoring ) {
		const shapeError = validateShape( draft );
		const wasClean =
			true === nfRadio.channel( 'app' ).request( 'get:setting', 'clean' );
		if ( shapeError ) {
			return {
				success: false,
				before: null,
				wasClean,
				error: new Error( shapeError ),
			};
		}

		const before = serialize( false );
		const fields = nfRadio.channel( 'fields' ).request( 'get:collection' );
		const actions = nfRadio
			.channel( 'actions' )
			.request( 'get:collection' );
		const fieldBookkeeping = captureBookkeeping( fields );
		const actionBookkeeping = captureBookkeeping( actions );

		let result;
		try {
			reconcile( draft, restoring );
			result = {
				success: true,
				before,
				wasClean,
				error: null,
			};
		} catch ( error ) {
			try {
				reconcile( before, true );
			} catch ( rollbackError ) {
				error.rollbackError = rollbackError;
			} finally {
				restoreBookkeeping( fields, fieldBookkeeping );
				restoreBookkeeping( actions, actionBookkeeping );
				nfRadio
					.channel( 'app' )
					.request( 'update:setting', 'clean', wasClean );
			}
			/*
			 * The reconciler's diagnostics name the exact stage and mismatch,
			 * while the chat surface can only offer the administrator general
			 * copy. Without this the detail dies here, and diagnosing a failed
			 * apply means rebuilding the bundle with instrumentation.
			 */
			if ( window.console && window.console.error ) {
				window.console.error(
					'Ninja Forms AI: the draft could not be applied.',
					error && error.message ? error.message : error,
					error && error.rollbackError
						? { rollbackError: error.rollbackError }
						: ''
				);
			}
			result = {
				success: false,
				before,
				wasClean,
				error,
			};
		}
		return result;
	};

	/**
	 * Apply a provider-produced draft whose only missing live rows must be
	 * explicitly marked as newly created by AI.
	 *
	 * @param {Object} draft Complete target draft.
	 * @return {{success: boolean, before: Object|null, wasClean: boolean, error: Error|null}} Result.
	 */
	const apply = function ( draft ) {
		return applyDraft( draft, false );
	};

	/**
	 * Restore a trusted browser snapshot, recreating rows removed after capture.
	 *
	 * @param {Object} draft Complete browser snapshot.
	 * @return {{success: boolean, before: Object|null, wasClean: boolean, error: Error|null}} Result.
	 */
	const restore = function ( draft ) {
		return applyDraft( draft, true );
	};

	return {
		apply,
		restore,
		serialize,
		validateShape,
	};
} );

